import { createListenerMiddleware } from '@reduxjs/toolkit';

import type { AppDispatch, RootState } from '@app/store/store';
import { cancelTrackedReservation } from '@features/token-management/utils/reservation-registry';
import { cancelInFlightRefresh } from '@shared/utils/access-token-refresh';
import { resetAllApiState } from '@shared/utils/login.utils';

import type { AuthBroadcastMessage } from '../utils/authBroadcast';
import { isBroadcastSuppressed, postAuthMessage, skipBroadcast } from '../utils/authBroadcast';

import { logout, setAuthToken, setCredentials } from './authentication.slice';

/**
 * OUTBOUND: Sends local auth changes to other open tabs via the BroadcastChannel.
 * * We ignore actions tagged with `skipBroadcast` to prevent an endless loop
 * (ping-pong effect) of messages between tabs.
 *
 * Note: `TOKEN_REFRESHED` is only broadcast after a REAL network `/refresh`.
 * If a tab just copies a new token from another tab while waiting in line,
 * it stays quiet and doesn't broadcast anything.
 */

export const authBroadcastListenerMiddleware = createListenerMiddleware();

const startListening = authBroadcastListenerMiddleware.startListening.withTypes<RootState, AppDispatch>();

startListening({
  actionCreator: setCredentials,
  effect: (action) => {
    if (isBroadcastSuppressed(action)) {
      return;
    }
    postAuthMessage({ type: 'LOGIN', payload: action.payload });
  },
});

startListening({
  actionCreator: setAuthToken,
  effect: (action) => {
    if (isBroadcastSuppressed(action)) {
      return;
    }
    postAuthMessage({ type: 'TOKEN_REFRESHED', token: action.payload });
  },
});

startListening({
  actionCreator: logout,
  effect: (action) => {
    if (isBroadcastSuppressed(action)) {
      return;
    }
    postAuthMessage({ type: 'LOGOUT' });
  },
});

/**
 * INBOUND: Processes messages received from other tabs.
 * * We wrap all local updates in `skipBroadcast` so we don't accidentally
 * echo them back to the sender.
 *
 * - `LOGIN`: Log the user in locally. If a different user or the same user, but on a different tenant just logged in
 * from another tab, we clear the local cache first so we don't mix up their data.
 * - `TOKEN_REFRESHED`: Update the access token in memory. (No need to clear
 * the cache because it's still the same user).
 * - `LOGOUT`: Stop any ongoing refresh requests, release the Web Lock,
 * clear the cache, and log out locally. We DO NOT call the backend `/logout` API
 * here because the tab that initiated the logout already did it.
 */

export interface ApplyRemoteAuthMessageParams {
  message: AuthBroadcastMessage;
  dispatch: AppDispatch;
  getState: () => RootState;
}

export const applyRemoteAuthMessage = async ({
  message,
  dispatch,
  getState,
}: ApplyRemoteAuthMessageParams): Promise<void> => {
  switch (message.type) {
    case 'LOGIN': {
      const currentAuth = getState().authentication;

      const isDifferentUser = currentAuth.username !== message.payload.username;
      const isDifferentTenant = (currentAuth.tenantId ?? '') !== (message.payload.tenantId ?? '');

      if (isDifferentUser || isDifferentTenant) {
        dispatch(resetAllApiState());
      }
      dispatch(skipBroadcast(setCredentials(message.payload)));
      break;
    }
    case 'TOKEN_REFRESHED': {
      dispatch(skipBroadcast(setAuthToken(message.token)));
      break;
    }
    case 'LOGOUT': {
      cancelInFlightRefresh();
      await cancelTrackedReservation(dispatch);
      dispatch(resetAllApiState());
      dispatch(skipBroadcast(logout()));
      break;
    }
  }
};
